// public-frontend/src/utils/visitorTracker.ts
/**
 * visitorTracker — home-page visit beacon (public / marketing site)
 * ─────────────────────────────────────────────────────────────────
 * Twin of frontend/src/utils/visitorTracker.ts; see that file for the full
 * rationale. The only difference: `app` is tagged 'public-frontend' so the
 * backend writes to <LOG_DIR>/visitors/public-frontend/ rather than .../frontend/.
 *
 * The backend derives IP, country, datacenter and bot-or-human. Everything sent
 * from here is browser self-report and is treated as such.
 *
 * Human gate (same contract as the product twin)
 * ----------------------------------------------
 * The first beacon of a session fires from HumanGate when the gate is shown
 * (`gateShown: true`), or from HomePage when a stored human pass let the
 * visitor straight in (`humanPass` is sent and re-verified server-side). The
 * session flag keeps it to one either way. `reportGateSkipped` is the only gate
 * outcome the browser reports itself; verified/failed rows are written by the
 * backend.
 */

import { getHumanPass } from './humanPass';

// ✅ CORS-safe base, same rule as LoginFormEnhanced/RegistrationForm.
//
// ⚠️ Do NOT switch this to `VITE_API_BASE || 'http://localhost:3001'` (the older
// App.tsx / contactApi.ts spelling). Production's public-frontend/.env sets
// VITE_API_URL='' and never defines VITE_API_BASE, so that form resolves to the
// literal http://localhost:3001 in the SHIPPED bundle — every visitor's browser
// would try to reach a server on THEIR machine. Chrome answers that with a
// "wants to access apps and services on this device" permission prompt on the
// home page, and the beacon fails either way.
//
// '' → relative '/api/visitors/visit' → same-origin through nginx (or the Vite
// proxy in dev). No CORS, no preflight, no local-network prompt.
const API_BASE = import.meta.env.VITE_API_URL ?? '';

const APP = 'public-frontend' as const;
const SESSION_FLAG = 'ssp_visit_logged_public';
const SESSION_ID_KEY = 'ssp_visit_session_id';

/** Stable id for this tab session — lets the admin view group repeat hits. */
export function sessionId(): string {
  try {
    let id = sessionStorage.getItem(SESSION_ID_KEY);
    if (!id) {
      id =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : Math.random().toString(36).slice(2) + Date.now().toString(36);
      sessionStorage.setItem(SESSION_ID_KEY, id);
    }
    return id;
  } catch {
    return '';
  }
}

/**
 * Record a home-page visit. Fires at most once per browser tab session unless
 * `force` is set.
 *
 * @param options.gateShown  the human gate is on screen for this load
 */
export function trackHomePageVisit(options: { force?: boolean; gateShown?: boolean } = {}): void {
  if (typeof window === 'undefined') return;

  try {
    if (!options.force && sessionStorage.getItem(SESSION_FLAG) === '1') return;
    sessionStorage.setItem(SESSION_FLAG, '1');
  } catch {
    // Storage disabled — send anyway; one extra row beats losing the visitor.
  }

  const payload = {
    app: APP,
    path: window.location.pathname + window.location.hash,
    referrer: document.referrer || '',
    sessionId: sessionId(),
    screen: `${window.screen?.width ?? 0}x${window.screen?.height ?? 0}`,
    language: navigator.language || '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
    event: 'load',
    gateShown: options.gateShown === true,
    humanPass: getHumanPass() ?? undefined,
  };

  send(payload);
}

/** The gate widget could not load and the visitor chose to continue anyway. */
export function reportGateSkipped(): void {
  if (typeof window === 'undefined') return;
  send({
    app: APP,
    path: window.location.pathname + window.location.hash,
    sessionId: sessionId(),
    event: 'gate-skipped',
  });
}

function send(payload: Record<string, unknown>): void {
  try {
    void fetch(`${API_BASE}/api/visitors/visit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
      credentials: 'omit',
    }).catch(() => undefined);
  } catch {
    /* never surface analytics failures */
  }
}

export default trackHomePageVisit;
